"use client";

import Image from "next/image";
import Link from "next/link";
import { Phone, ArrowRight, Check, X, AlertTriangle, Shield, ExternalLink } from "lucide-react";
import { BRAND } from "@/lib/brand";

const F = { display: "'Plus Jakarta Sans', sans-serif", body: '"Barlow", sans-serif', mono: '"DM Mono", monospace' };
const C = { gold: "#D4A84B", white: "#ffffff", w82: "rgba(255,255,255,0.82)", w55: "rgba(255,255,255,0.70)", w30: "rgba(255,255,255,0.30)", navy: "#080F1A", navyMid: "#0d1929", red: "rgba(220,38,38,0.9)" };
const CARD: React.CSSProperties = { background: "rgba(255,255,255,0.10)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.15)", borderTop: `2px solid ${C.gold}`, borderRadius: 14, padding: "2rem", display: "flex", flexDirection: "column" };
const PANEL: React.CSSProperties = { background: "rgba(255,255,255,0.12)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "2.5rem" };
const LABEL: React.CSSProperties = { color: C.gold, fontFamily: F.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", display: "block", marginBottom: "0.6rem" };
const H2: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", marginBottom: "0.75rem", lineHeight: 1.1 };
const H3: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.25rem", marginBottom: "0.75rem" };
const P: React.CSSProperties = { color: C.w82, fontSize: "0.92rem", lineHeight: 1.7, fontFamily: F.body };
const OVERLAY = (o: number): React.CSSProperties => ({ position: "absolute", inset: 0, background: `rgba(5,13,24,${o})`, zIndex: 1 });
const CONTENT: React.CSSProperties = { position: "relative", zIndex: 2, padding: "5rem 1.5rem", maxWidth: 1200, margin: "0 auto", width: "100%" };
const SEC: React.CSSProperties = { position: "relative", overflow: "hidden", backgroundColor: C.navy };
const GOLDLINE: React.CSSProperties = { width: 48, height: 2, background: C.gold, borderRadius: 2, margin: "16px 0" };
const BTN_RED: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(135deg, #DC2626 0%, #dc2626 100%)", color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.1rem", borderRadius: 12, padding: "16px 36px", textDecoration: "none", border: "none", letterSpacing: "1px", boxShadow: "0 8px 32px rgba(220,38,38,0.55), 0 2px 8px rgba(220,38,38,0.4)" };
const BTN_GOLD: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: C.gold, fontFamily: F.body, fontWeight: 600, fontSize: "1rem", border: `1.5px solid ${C.gold}`, borderRadius: 10, padding: "13px 26px", textDecoration: "none" };

// NOTE: the FAQPage JSON-LD in layout.tsx mirrors this list (q → question,
// drr → answer). Keep both in sync when editing checklist copy.
const checklist = [
  { q: "Are you licensed in Arizona?", red: "We're licensed in our home state.", drr: "AZ ROC KB-1 Dual #349012 — verify right now at roc.az.gov" },
  { q: "Can I verify your license online?", red: "Hesitation, excuses, \"we're in the process.\"", drr: "Yes. roc.az.gov. Takes 30 seconds." },
  { q: "Are your workers your employees or subcontractors?", red: "We use trusted subs.", drr: "DRR employees. Same people start to finish." },
  { q: "Are you IICRC certified?", red: "We follow IICRC standards. (Not the same as certified.)", drr: "Yes. IICRC certified. Ask for the certificate." },
  { q: "Will you give me a written scope before work begins?", red: "We'll assess as we go.", drr: "Xactimate Level 3 scope in writing before a dollar is spent." },
  { q: "Who is my point of contact throughout the project?", red: "Someone from our team will be in touch.", drr: "One project manager. One number. Every day." },
  { q: "Have you worked with my insurance carrier before?", red: "We'll figure it out.", drr: "We communicate directly with your carrier. We speak their language." },
  { q: "Can you show me proof of insurance?", red: "Delay, \"we'll send it later.\"", drr: "Certificate of insurance provided before mobilization." },
  { q: "Do you have experience with this type of loss?", red: "All water damage is the same.", drr: "Category 1/2/3, Class I–IV, ICRA compliant, large-loss commercial — ask us specifically." },
  { q: "Can you provide references?", red: "Check our website reviews.", drr: "References available upon request. Banner Health, City of Goodyear, and others on file." },
];

export default function HowToChoosePage() {
  return (
    <>
      {/* ═══ HERO ═══ */}
      <section style={{ ...SEC, minHeight: "75vh", display: "flex", alignItems: "center" }}>
        <Image src="/images/arizona-landmarks/havasupai-falls-arizona.jpg" alt="Arizona property" fill style={{ objectFit: "cover" }} priority />
        <div style={OVERLAY(0.60)} />
        <div style={{ ...CONTENT, maxWidth: 900, textAlign: "center" }}>
          <span style={LABEL}>Homeowner & Property Manager Resource</span>
          <h1 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3.8rem)", lineHeight: 1.05, marginBottom: 20 }}>
            How to Choose a Restoration Contractor in Arizona.<br />A 10-Point Checklist.
          </h1>
          <div style={{ ...GOLDLINE, margin: "20px auto" }} />
          <p style={{ ...P, fontSize: "1.1rem", maxWidth: 720, margin: "0 auto" }}>
            After a water or fire loss, you&rsquo;ll get calls from contractors you&rsquo;ve never heard of.
            Here&rsquo;s exactly what to ask — and what the answers should sound like.
          </p>
        </div>
      </section>

      {/* ═══ THE 10-POINT CHECKLIST ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={CONTENT}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span style={LABEL}>The Checklist</span>
            <h2 style={H2}>10 Questions. The Answers Should Be Clear.</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            {checklist.map((item, i) => (
              <div key={i} style={{ ...CARD, flexDirection: "column", padding: "1.75rem" }}>
                {/* Question header */}
                <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginBottom: 18 }}>
                  <span style={{ color: C.gold, fontFamily: F.display, fontWeight: 800, fontSize: "2.25rem", lineHeight: 1, flexShrink: 0, minWidth: 48 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 style={{ ...H3, marginBottom: 0, paddingTop: 6 }}>{item.q}</h3>
                </div>

                {/* Answers grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
                  {/* Red Flag */}
                  <div style={{ background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.22)", borderRadius: 10, padding: "1rem 1.25rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <X size={16} style={{ color: C.red }} />
                      <span style={{ fontFamily: F.mono, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: C.red, fontWeight: 600 }}>Red Flag Answer</span>
                    </div>
                    <p style={{ ...P, fontSize: "0.88rem", fontStyle: "italic", color: C.w82 }}>&ldquo;{item.red}&rdquo;</p>
                  </div>

                  {/* DRR Answer */}
                  <div style={{ background: "rgba(212,168,75,0.08)", border: "1px solid rgba(212,168,75,0.28)", borderRadius: 10, padding: "1rem 1.25rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <Check size={16} style={{ color: C.gold }} />
                      <span style={{ fontFamily: F.mono, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: C.gold, fontWeight: 600 }}>DRR&rsquo;s Answer</span>
                    </div>
                    <p style={{ ...P, fontSize: "0.88rem", color: C.white }}>{item.drr}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CONSUMER PROTECTION CALLOUT ═══ */}
      <section style={{ ...SEC, padding: "60px 24px" }}>
        <div style={{ maxWidth: 850, margin: "0 auto" }}>
          <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.22)", borderRadius: 14, padding: "2rem 2.5rem" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <AlertTriangle size={32} style={{ color: "#D4A84B", flexShrink: 0, marginTop: 4 }} />
              <div style={{ flex: 1, minWidth: 260 }}>
                <span style={{ ...LABEL, marginBottom: 10 }}>Arizona AG Consumer Alert</span>
                <h3 style={{ ...H3, fontSize: "1.25rem" }}>After Storms, Unlicensed Operators Flood Arizona</h3>
                <p style={{ ...P, marginBottom: 14 }}>
                  After monsoon and storm events, unlicensed contractors actively solicit Arizona homeowners
                  and businesses. Never sign a contract without verifying the license at{" "}
                  <a href="https://roc.az.gov" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontWeight: 600, textDecoration: "underline" }}>roc.az.gov</a>.
                  If a contractor can&rsquo;t be verified, walk away.
                </p>
                <a href="https://roc.az.gov" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontSize: "0.85rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                  Verify a License at roc.az.gov <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DRR SUMMARY BOX ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid, padding: "60px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ ...PANEL, padding: "2.5rem", textAlign: "center" }}>
            <Shield size={40} style={{ color: C.gold, margin: "0 auto 16px" }} />
            <span style={{ ...LABEL, marginBottom: 12 }}>The Result</span>
            <h2 style={{ ...H2, fontSize: "clamp(1.6rem, 3vw, 2.4rem)", marginBottom: 20 }}>
              DRR Passes All 10.
            </h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginBottom: 20 }}>
              {[
                "AZ ROC #349012",
                "IICRC Certified",
                "BBB A+ Accredited",
                `${BRAND.rating}★ ${BRAND.reviewCount} Reviews`,
                "Licensed",
                "Bonded",
                "Insured",
              ].map((tag) => (
                <span key={tag} style={{ background: "rgba(212,168,75,0.12)", border: "1px solid rgba(212,168,75,0.28)", borderRadius: 20, padding: "6px 14px", fontFamily: F.mono, fontSize: "10px", letterSpacing: "1.5px", textTransform: "uppercase", color: C.gold, fontWeight: 600 }}>
                  {tag}
                </span>
              ))}
            </div>
            <a href="https://roc.az.gov" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontSize: "0.9rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
              Verify everything at roc.az.gov <ExternalLink size={14} />
            </a>
            <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
              <Link href="/about/our-guarantee" style={{ ...BTN_GOLD, fontSize: "0.9rem", padding: "10px 20px" }}>
                See Our 8 Commitments <ArrowRight size={14} />
              </Link>
              <Link href="/who-we-help/residential" style={{ ...BTN_GOLD, fontSize: "0.9rem", padding: "10px 20px" }}>
                Residential Services <ArrowRight size={14} />
              </Link>
              <Link href="/who-we-help/commercial" style={{ ...BTN_GOLD, fontSize: "0.9rem", padding: "10px 20px" }}>
                Commercial Services <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ ...SEC, minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image src="/images/arizona-landmarks/monument-valley-night-arizona.jpg" alt="Arizona night" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.55)} />
        <div style={{ ...CONTENT, maxWidth: 750, textAlign: "center" }}>
          <div style={{ ...PANEL, padding: "56px 40px" }}>
            <span style={{ ...LABEL, marginBottom: 20 }}>AVAILABLE 24 · 7 · 365</span>
            <h2 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(1.8rem, 4vw, 3rem)", lineHeight: 1.08, marginBottom: 16 }}>
              Ask us any of these questions.<br />We have the answers.
            </h2>
            <p style={{ ...P, fontSize: "1rem", marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
              From day one to the day the project is completed. Verifiable credentials. Transparent process.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              </div>
            <p style={{ marginTop: 28, fontFamily: F.mono, fontSize: "0.62rem", letterSpacing: "2px", textTransform: "uppercase", color: C.w30 }}>
              AZ ROC #349012 · IICRC · BBB A+ · Verify at roc.az.gov
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
